import merge from 'lodash/merge';
import snakeCase from 'lodash/snakeCase';

import { createAxiosInstance } from '@/apis/axios.utils';
import { createExternalAxiosInstance } from '@/apis/external-apis';
import { env } from '@/env';
import type {
  ActionMetadata as GeneratedActionMetadata,
  AddNewBrandUserRequest,
  BrandCompact1,
  BrandPlanTrialDates,
  BrandTag1,
  BrandUser,
  BrandUserDetail,
  BusinessHoursResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  DHProductSubscription,
  GetApiBrandsBrandIdUsersParams,
  GetApiBrandsParams,
  GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams,
  GetApiUsersParams,
  GetCompanyCustomerInfo,
  HubspotVisitorTokenResponse,
  IntegrationDetails,
  ListApiKeysResponse,
  ListBrandUsers,
  PasswordResetsResponse,
  PublicPlatformConnection,
  SelfIntegrationsResponse,
  StripeCheckoutSession,
  SubscriptionSourceData,
  TaskResponse,
  Token,
  UpdateBrandUserRequest,
  UpsertBusinessHoursRequest,
  User1,
  UserDetail,
} from '@/generated/auth/model';
import type { ActionMetadata, RawIdentity, Subscriptions } from '@/types/auth.types';
import type { AxiosRequestConfig } from '@/types/axios';
import type { CamelizeKeys } from '@/types/camelize';

const axios = createAxiosInstance(env.authApiUrl);
const noAuthAxios = createAxiosInstance(env.authApiUrl, {
  includeAuth: false,
});
const simpleAxios = createExternalAxiosInstance({
  baseURL: env.authApiUrl,
});

export async function getSelf(axiosConfig: AxiosRequestConfig = {}) {
  return axios.get<RawIdentity>('/self', {
    ...axiosConfig,
  });
}

export async function validateApiKey(
  { token }: { token?: string } = {},
  axiosConfig: AxiosRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Using an external axios instance here since we don't want impersonation or to logout if this fails
  return simpleAxios.get<RawIdentity>('/self', {
    headers,
    ...axiosConfig,
  });
}

export async function getSelfSubscriptions(
  { token }: { token?: string } = {},
  axiosConfig: AxiosRequestConfig = {},
) {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return axios.get<Subscriptions>('/self/subscriptions', {
    headers,
    camelizeKeys: true,
    ...axiosConfig,
  });
}

export async function getSelfActionMetadata(axiosConfig: AxiosRequestConfig = {}) {
  return axios.get<ActionMetadata>('/self/action_metadata', {
    camelizeKeys: true,
    ...axiosConfig,
  });
}

export interface CreateTokenParams {
  email?: string;
  password?: string;
  oauth?: unknown;
  otpCode?: string;
  ssoToken?: string;
}

export async function createToken(
  { email, password, oauth, otpCode, ssoToken }: CreateTokenParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<Token>(
    '/tokens',
    {
      email,
      password,
      oauth,
      otpCode,
      ssoToken,
    },
    merge({ withCredentials: true }, axiosConfig),
  );
}

export async function destroyToken(axiosConfig: AxiosRequestConfig = {}) {
  return axios.delete<void>('/tokens/self', {
    withCredentials: true,
    ...axiosConfig,
  });
}

export async function getUserDetails(
  { userId }: { userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<UserDetail>(`/users/${userId}`, axiosConfig);
}

export async function updateUser(
  { userId, data }: { userId: number; data: Partial<CamelizeKeys<UserDetail>> },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.patch<UserDetail>(`/users/${userId}`, data, axiosConfig);
}

export async function resetUserPassword(
  { userId, hash, password }: { userId: number; hash: string; password: string },
  axiosConfig: AxiosRequestConfig = {},
) {
  return noAuthAxios.post<void>(`/users/${userId}/reset_password`, { hash, password }, axiosConfig);
}

export async function changeUserPassword(
  { userId, password, oldPassword }: { userId: number; password: string; oldPassword: string },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<void>(
    `/users/${userId}/change_password`,
    { password, oldPassword },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function getUsers(
  { params }: { params: Partial<CamelizeKeys<GetApiUsersParams>> },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<User1[]>('/users', { ...axiosConfig, params });
}

export async function getBrands(
  { params }: { params: Partial<CamelizeKeys<GetApiBrandsParams>> },
  axiosConfig: AxiosRequestConfig = {},
) {
  return noAuthAxios.get<BrandCompact1[]>('/brands', merge({ params }, axiosConfig));
}

export async function getBrandIntegrations(
  { brandId }: { brandId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<IntegrationDetails>>(
    `/brands/${brandId}/integrations`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function getUserIntegrations(axiosConfig: AxiosRequestConfig = {}) {
  return axios.get<CamelizeKeys<SelfIntegrationsResponse>>(
    `/self/integrations`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function postOAuth(
  { provider, redirectUri, brandId }: { provider: string; redirectUri: string; brandId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  const providerFormatted = provider === 'pinterest_v5' ? 'pinterest_v5' : snakeCase(provider);
  return axios.post(
    `/${providerFormatted}/oauth`,
    {
      redirectUri,
      brandId,
    },
    axiosConfig,
  );
}

export async function getBrandUsers(
  {
    brandId,
    limit,
    offset = 0,
    email,
    search,
    sort,
    includeRestricted,
  }: { brandId: number } & Partial<CamelizeKeys<GetApiBrandsBrandIdUsersParams>>,
  axiosConfig: AxiosRequestConfig = {},
) {
  const params: Record<string, unknown> = {
    limit,
    offset,
    includeRestricted,
  };
  if (email) {
    params.email = email;
  }
  if (search) {
    params.search = search;
  }
  if (sort) {
    params.sort = sort;
  }
  return axios.get<CamelizeKeys<ListBrandUsers>>(
    `/brands/${brandId}/users`,
    merge(
      { camelizeKeys: true },
      {
        params,
      },
      axiosConfig,
    ),
  );
}

export async function createBrandUser(
  {
    brandId,
    email,
    permissions,
    role,
  }: {
    brandId: number;
    email: string;
    permissions: CamelizeKeys<AddNewBrandUserRequest>['permissions'];
    role: string;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<BrandUser>(
    `/brands/${brandId}/users`,
    {
      email,
      permissions,
      role,
    },
    axiosConfig,
  );
}

export async function getBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<BrandUserDetail>(`/brands/${brandId}/users/${userId}`, axiosConfig);
}

export async function updateBrandUser(
  {
    brandId,
    userId,
    role,
    permissions,
  }: {
    brandId: number;
    userId: number;
    role: string;
    permissions: CamelizeKeys<UpdateBrandUserRequest>['permissions'];
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.patch<BrandUserDetail>(
    `/brands/${brandId}/users/${userId}`,
    {
      role,
      permissions,
    },
    axiosConfig,
  );
}

export async function removeBrandUser(
  { brandId, userId }: { brandId: number; userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete<void>(`/brands/${brandId}/users/${userId}`, axiosConfig);
}

export async function resendBrandUserInvitation(
  { brandId, userId }: { brandId: number; userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<void>(`/brands/${brandId}/users/${userId}/resend_invitation`, axiosConfig);
}

export async function getBrandUserExportUserList(
  { userId }: { userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<TaskResponse>(`/brands/user/${userId}/exports/user_list`, axiosConfig);
}

export async function postSamlAssertion(
  { samlRequest, samlRelayState }: { samlRequest: string; samlRelayState: string },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post(
    '/saml_assertions',
    {
      samlRequest,
      relayState: samlRelayState,
    },
    axiosConfig,
  );
}

export async function generateApiKey(
  { brandId, label }: { brandId: number } & CamelizeKeys<CreateApiKeyRequest>,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CreateApiKeyResponse>(`/brands/${brandId}/api_keys`, { label }, axiosConfig);
}

export async function getApiKeysForBrand(
  { brandId }: { brandId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<ListApiKeysResponse>[]>(
    `/brands/${brandId}/api_keys`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function deleteApiKeyForBrand(
  { brandId, apiKeyId }: { brandId: number; apiKeyId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete<void>(`/brands/${brandId}/api_keys/${apiKeyId}`, axiosConfig);
}

export async function generatePasswordReset(
  { email }: { email: string },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<void>('password_resets', { email }, axiosConfig);
}

export async function getPasswordResetStatus(
  { hash, userId }: { hash: string; userId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<PasswordResetsResponse>(
    `password_resets/${hash}`,
    merge({ params: { userId } }, axiosConfig),
  );
}

export interface GetPlatformConnectionsParams {
  brandIds?: number | number[] | string;
  brandId?: number;
  brandToken?: string;
  platformType?: string;
}

export async function getPlatformConnections(
  { brandIds, brandId, brandToken, platformType }: GetPlatformConnectionsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (brandId) {
    params.brandId = brandId;
    params.brandToken = brandToken;
    params.platformType = platformType;
  } else if (brandIds) {
    params.brandIds = Array.isArray(brandIds) ? brandIds.join(',') : brandIds;
  }
  const axiosInstance = brandToken ? noAuthAxios : axios;
  return axiosInstance.get<PublicPlatformConnection[]>(
    `/platform_connections`,
    merge({ params }, axiosConfig),
  );
}

export interface CreatePlatformConnectionParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
  sourceCreatorId?: number;
  creatorHandleName?: string;
  page?: unknown;
}

export async function createPlatformConnection(
  {
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
    sourceCreatorId,
    creatorHandleName,
    page,
  }: CreatePlatformConnectionParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthAxios : axios;
  return axiosInstance.post<PublicPlatformConnection>(
    `/platform_connections`,
    {
      brandId,
      oauth,
      brandToken,
      platformType,
      invitedBy,
      page,
      // Used in TikTok creator OAuth flow
      sourceCreatorId,
      creatorHandleName,
    },
    axiosConfig,
  );
}

export interface UpdatePlatformConnectionParams {
  connectionId: number;
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function updatePlatformConnection(
  {
    connectionId,
    brandId,
    oauth,
    brandToken,
    platformType,
    invitedBy,
  }: UpdatePlatformConnectionParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthAxios : axios;
  return axiosInstance.patch<PublicPlatformConnection>(
    `/platform_connections/${connectionId}`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    axiosConfig,
  );
}

export async function deletePlatformConnection(
  { connectionId }: { connectionId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete<void>(`/platform_connections/${connectionId}`, axiosConfig);
}

export interface GetAccountsParams {
  brandId: number;
  oauth?: unknown;
  brandToken?: string;
  platformType: string;
  invitedBy?: string;
}

export async function getAccounts(
  { brandId, oauth, brandToken, platformType, invitedBy }: GetAccountsParams,
  axiosConfig: AxiosRequestConfig = {},
) {
  const axiosInstance = brandToken ? noAuthAxios : axios;
  return axiosInstance.post<void>(
    `/platform_connections/accounts`,
    { brandId, oauth, brandToken, platformType, invitedBy },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function createBillingCheckoutSession(
  {
    brandId,
    priceLookupKey,
    redirectDomain,
  }: {
    brandId: number;
    priceLookupKey: string;
    redirectDomain: string;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CamelizeKeys<StripeCheckoutSession>>(
    `/billing/checkout_sessions`,
    { brandId, priceLookupKey, redirectDomain },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function setBrandTrialDates(
  { brandId, dhProductSubscriptionId }: { brandId: number; dhProductSubscriptionId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CamelizeKeys<BrandPlanTrialDates>>(
    `/brands/${brandId}/trial`,
    { dhProductSubscriptionId },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function setActionMetadata(
  {
    brandId,
    actionContext,
    payload,
  }: {
    brandId: number;
    actionContext: string;
    payload: Record<string, unknown>;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.patch<GeneratedActionMetadata>(
    `/brands/${brandId}/action_metadata/${actionContext}`,
    payload,
    merge({ camelizeKeys: false }, axiosConfig),
  );
}

export async function getBrandTags(
  { organizationId }: { organizationId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<BrandTag1>[]>(
    `/organizations/${organizationId}/brand_tags`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function createBrandTag(
  { organizationId, name, color }: { organizationId: number; name: string; color: string },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CamelizeKeys<BrandTag1>>(
    `/organizations/${organizationId}/brand_tags`,
    { name, color },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function createBrandTagAssociations(
  {
    organizationId,
    brandIds,
    brandTagIds,
  }: {
    organizationId: number;
    brandIds: number[];
    brandTagIds: number[];
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  const requests: Promise<unknown>[] = [];
  const chunkSize = 10;
  for (let i = 0; i < brandIds.length; i += chunkSize) {
    const brandIdsChunk = brandIds.slice(i, i + chunkSize);
    requests.push(
      axios.post(
        `/organizations/${organizationId}/brand_associations`,
        { brandIds: brandIdsChunk, brandTagIds },
        merge({ camelizeKeys: true }, axiosConfig),
      ),
    );
  }
  await Promise.all(requests);
  return true;
}

export async function removeTagFromBrand(
  {
    organizationId,
    brandId,
    brandTagId,
  }: {
    organizationId: number;
    brandId: number;
    brandTagId: number;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete<void>(
    `/organizations/${organizationId}/brands/${brandId}/tags/${brandTagId}`,
    axiosConfig,
  );
}

export async function deleteBrandTag(
  { organizationId, brandTagId }: { organizationId: number; brandTagId: number },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.delete<void>(
    `/organizations/${organizationId}/brand_tags/${brandTagId}`,
    axiosConfig,
  );
}

export async function updateSubscriptionItem(
  {
    organizationId,
    priceLookupKey,
    subscriptionId = null,
  }: {
    organizationId: number;
    priceLookupKey: string;
    subscriptionId?: number | null;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  const data: { priceLookupKey: string; subscriptionId?: number | null } = { priceLookupKey };
  if (subscriptionId) {
    data.subscriptionId = subscriptionId;
  }
  return axios.post<void>(
    `/organizations/${organizationId}/subscription_items`,
    data,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function getOrganizationSubscriptions(
  {
    organizationId,
    params = { brandId: null },
  }: {
    organizationId: number;
    params?: CamelizeKeys<GetApiOrganizationsOrganizationIdDhProductSubscriptionsParams>;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<DHProductSubscription>[]>(
    `/organizations/${organizationId}/dh_product_subscriptions`,
    merge({ camelizeKeys: true }, { params }, axiosConfig),
  );
}

export async function getSubscriptionSourceData(
  { organizationId, sourceIds }: { organizationId: number; sourceIds: number[] },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<SubscriptionSourceData>[]>(
    `/organizations/${organizationId}/subscription_source_data`,
    merge({ camelizeKeys: true }, { params: { sourceIds: sourceIds.join(',') } }, axiosConfig),
  );
}

export async function getCompanyCustomerInformation(
  companyId: number,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.get<CamelizeKeys<GetCompanyCustomerInfo>>(
    `/companies/${companyId}/customer_information`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function createTrialSubscription(
  {
    organizationId = null,
    brandId = null,
    dhProductLabel,
  }: {
    organizationId?: number | null;
    brandId?: number | null;
    dhProductLabel: string;
  },
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CamelizeKeys<DHProductSubscription>>(
    `/dh_product_subscriptions/start_trial`,
    { brandId, organizationId, dhProductLabel },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function getHubspotSupportToken(axiosConfig: AxiosRequestConfig = {}) {
  return axios.post<CamelizeKeys<HubspotVisitorTokenResponse>>(
    `/self/hubspot_visitor_token`,
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function getBusinessHours(
  { brandIds }: { brandIds: number[] | string },
  axiosConfig: AxiosRequestConfig = {},
) {
  const params = { brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds };
  return axios.get<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    merge({ params, camelizeKeys: true }, axiosConfig),
  );
}

export async function createBusinessHours(
  { brandIds, hoursConfig, timezone }: CamelizeKeys<UpsertBusinessHoursRequest>,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.post<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}

export async function updateBusinessHours(
  { brandIds, hoursConfig, timezone }: CamelizeKeys<UpsertBusinessHoursRequest>,
  axiosConfig: AxiosRequestConfig = {},
) {
  return axios.put<CamelizeKeys<BusinessHoursResponse>[]>(
    '/business_hours',
    { brandIds, hoursConfig, timezone },
    merge({ camelizeKeys: true }, axiosConfig),
  );
}
